<template>
  <div class='technologies-tab'>
    <div class="technologies-tab__header">
      <div class='technologies-tab__header__left'>
        <div class='technologies-tab__header__left__filter'>
          <filtered-search
            class='search-filter'
            :search-item='search'
            :filters="filters"
            :show-filters="true"
            @searchInput="searchChange"
            @filtersInput="filtersChange"
            tab="technologies"
          />
          <div class="technologies-tab__header__left__filter__switch">
            <v-switch
              v-model="hasExploit"
              :label="$t('configuration.has.exploit')"
            ></v-switch>
          </div>
        </div>
      </div>
      <div class='technologies-tab__header__right'>
        <custom-tooltip popper-class="light-tooltip">
          <div slot='buttonContent'>
            <download-c-s-v class="technologies-tab__header__right__csv" :class="{'disabled': dataLoading || technologiesLoading}" @click="downloadCSVReport"/>
          </div>
          <div slot="content">
            <slot name="tooltip">
              {{ $t('domain.insights.twisted.domains.download.csv.tooltip') }}
            </slot>
          </div>
        </custom-tooltip>
      </div>
    </div>
    <div class='technologies-content' v-if='!dataLoading && !technologiesLoading'>
      <div v-if='technologiesFilteredItems.length'>
        <wombat-table
          class="technologies-table"
          :items="technologiesFilteredItems"
          :columns="tableColumnsTechnologies"
          :resize="false"
          :loadingItems="false"
          :columnsReorder="false"
          :itemHeight="58"
          :noResultsMessage="$t('noResultsFound')"
          @columnSorted="columnSorted"
        >
          <component
            slot="row-cell"
            slot-scope="rowCell"
            class="row-cell"
            :is="tableRowCellComponentsHashTechnologies[rowCell.column.name] || tableRowCellComponentsHashTechnologies.default"
            :item="rowCell.item"
            :column="rowCell.column"
            @toggleCVEsListPopup='toggleCVEsListPopup'
            @toggleAssetsListPopup='toggleAssetsListPopup'
            @integrationClicked='integrationClicked'
          />
        </wombat-table>
        <div class="technologies-tab__footer">
          <advanced-pagination
            tab='technologies'
            :pages-amount='pages'
            :page="page"
            :is-show-pagination='isShowPagination'
            @changedSelectedPage='changedSelectedPage'
            @changedItemsAmountPerPage='changedItemsAmountPerPage'
          />
        </div>
      </div>
      <no-data
        v-else
        :message='messageForNoData.toString()'
      />
    </div>
    <div class='techs-tab-loading' v-else />
    <page-loading-overlay
      :show="technologiesLoading && currentTab === 'technologies'"
      :progress="technologiesProgress"
      contained
    />
    <assets-list-right-popup
      :technology-item='currentTechnology'
      :internalValue='assetsListPopup'
      @closed='assetsListPopup = false'
    />
    <cves-amount-popup
      :technology-item='currentTechnology'
      :internalValue='cvesAmountPopup'
      @closed='cvesAmountPopup = false'
    />
    <ticketing-integration-popup
      :technology-item='currentTechnology'
      :internalValue='showTicketingIntegrationPopup'
      title='technologies.jira.integration.popup.title'
      message='technologies.jira.integration.popup.pending.message'
      ok-caption='technologies.jira.integration.popup.ok'
      @clicked='showTicketingIntegrationPopup = false'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getEmptyColumn, technologiesTabTableColumns } from '@/services/tableColumns';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import TechnologyNameTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologyNameTableCell.vue';
import TechnologyVersionTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologyVersionTableCell.vue';
import TechnologyAssetsAmountTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologyAssetsAmountTableCell.vue';
import TechnologyExploitIndicationTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologyExploitIndicationTableCell.vue';
import TechnologyTrendTableCell from '@/components/tableCells/CveDataTableCells/CveTrendTableCell.vue';
import TechnologyValidationTriggeredTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologyValidationTriggeredTableCell.vue';
// bugfix/CYN-1789-multiple-ui-currections, remove validation icon from row
// import TechnologyValidateActionTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologyValidateActionTableCell.vue';
// eslint-disable-next-line no-unused-vars
import indexDB from '@/assets/tech-metadata/index-db.json';
import AssetsListRightPopup from '@/components/popup/AssetsListRightPopup.vue';
import TechnologiesCvesAmountTableCell from '@/components/tableCells/TechnologiesTabTableCells/TechnologiesCvesAmountTableCell.vue';
import CvesAmountPopup from '@/components/popup/CvesAmontPopup.vue';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import TicketingIntegrationPopup from '@/components/popup/TicketingIntegrationPopup.vue';
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import DownloadCSV from '@/assets/icons/download_csv.svg?inline';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import NoData from '@/components/common/NoData.vue';
import {TECHNOLOGIES_HEADERS} from '@/constants/configuration';
import {CHANGE_TECHNOLOGIES_LIST_OPTIONS} from '@/store/configuration/actionTypes';
import {downloadCSVReportByTab} from '@/services/repository';

export default {
  name: 'TechnologiesTab',
  props: {
    currentTab: {
      required: false,
    },
    hasFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    NoData,
    TicketingIntegrationPopup,
    AdvancedPagination,
    PageLoadingOverlay,
    CvesAmountPopup,
    TechnologiesCvesAmountTableCell,
    WombatTable,
    TechnologyNameTableCell,
    AssetsListRightPopup,
    FilteredSearch,
    DownloadCSV,
    CustomTooltip,
  },
  data() {
    return {
      cvesAmountPopup: false,
      assetsListPopup: false,
      showTicketingIntegrationPopup: false,
      currentTechnology: {name: ''},
      filters: [{name: 'Asset', attr: 'asset'}, {name: 'CVE', attr: 'cve'}, {name: 'CVE Score >', attr: 'cve score >'}],
      search: '',
      filterSearch: [],
      sortTechnologies: {
        [TECHNOLOGIES_HEADERS['technologyTabTable.technology']]: 'desc',
      },
      hasExploit: false,
      page: 1,
      callCounter: 0,
      polling: null,
      approveLoadingOnSearchChange: false,
      firstTimeCalled: false,
    };
  },
  async beforeDestroy() {
    clearInterval(this.polling);
  },
  methods: {
    async loadTechnologies() {
      if (this.currentTab === 'technologies') {
        clearInterval(this.polling);
        await this.$store.dispatch(CHANGE_TECHNOLOGIES_LIST_OPTIONS, {
          sort: this.sortTechnologies,
          limit: this.itemsPerPage,
          page: this.page,
          search: this.search,
          filter: this.filterSearch,
          hasExploit: this.hasExploit,
          domain: this.domainFilter || [],
          organizations: this.organizationFilter || [],
          turnOnLoading: true,
          tab: 'technologies',
        });
        if (this.technologiesRefId && this.technologiesRefId !== '') {
          this.polling = setInterval(async () => {
            if (!this.technologiesRefId || this.technologiesRefId === '') {
              clearInterval(this.polling);
            } else if (!this.technologiesRunningInterval) {
              await this.$store.dispatch(CHANGE_TECHNOLOGIES_LIST_OPTIONS, {
                sort: this.sortTechnologies,
                limit: this.itemsPerPage,
                page: this.page,
                search: this.search,
                filter: this.filterSearch,
                hasExploit: this.hasExploit,
                domain: this.domainFilter || [],
                organizations: this.organizationFilter || [],
                turnOnLoading: true,
                tab: 'technologies',
                refId: this.technologiesRefId,
              });
            }
          }, 3000);
        }
      }
    },
    async searchChange(e) {
      this.search = e.trim();
      this.search = this.extractSearchString();
      this.page = 1;
      if (this.approveLoadingOnSearchChange || this.search.length) {
        await this.loadTechnologies();
      }
      if (!this.approveLoadingOnSearchChange) {
        this.approveLoadingOnSearchChange = true;
      }
    },
    async filtersChange(e) {
      this.filterSearch = e;
      this.filterSearch = this.extractFilters();
      this.page = 1;
      await this.loadTechnologies();
    },
    extractSearchString() {
      if (!this.search) {
        return '';
      }
      let results = this.search.split(' ');
      results = results.filter(result => !result.includes(':'));
      return results.join(' ').trim();
    },
    extractFilters() {
      const filtersMap = new Map();
      if (this.search) {
        let results = this.search.split(' ');
        results = results.filter(result => result.includes(':'));
        results.forEach(item => {
          const filter = item.split(':');
          if (filter.length > 1 && filter[1].length > 0) {
            filtersMap.set(filter[0], filter[1].split(',').map(element => element.trim()));
          }
        });
      }
      if (this.filterSearch.length > 0) {
        this.filterSearch.forEach(item => {
          const filter = item.split(':');
          if (filtersMap.has(filter[0])) {
            const filtersValues = filtersMap.get(filter[0]);
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          } else {
            const filtersValues = [];
            filter[1].split(',').forEach(value => {
              filtersValues.push(value.trim());
            });
            filtersMap.set(filter[0], filtersValues);
          }
        });
      }
      return filtersMap;
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'technologies'});
      await this.loadTechnologies();
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadTechnologies();
    },
    toggleCVEsListPopup(technology) {
      this.currentTechnology = technology;
      this.cvesAmountPopup = true;
    },
    toggleAssetsListPopup(technology) {
      this.currentTechnology = technology;
      this.assetsListPopup = true;
    },
    integrationClicked(technology) {
      this.currentTechnology = technology;
      this.showTicketingIntegrationPopup = true;
    },
    async columnSorted({ column, direction }) {
      this.sortTechnologies = {
        [column.sortingField || column.field]: direction,
      };
    },
    async downloadCSVReport() {
      const payload = {
        sort: this.sortTechnologies,
        limit: this.itemsPerPage,
        page: this.page,
        search: this.search,
        filter: this.filterSearch,
        toggles: {
          hasExploit: this.hasExploit,
        },
        domain: this.domainFilter || [],
        organizations: this.organizationFilter || [],
        turnOnLoading: true,
        tab: 'technologies',
      };
      const dataStream = await downloadCSVReportByTab(payload);
      const blob = new Blob([dataStream], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `${Date.now()}-technologies.csv`);
      document.body.appendChild(link);
      link.click();
    },
  },
  computed: {
    ...mapGetters([
      'paginationItemsPerPageByTabs',
      'dataLoading',
      'technologiesLoading',
      'technologiesListSort',
      'technologiesPaginationState',
      'domainFilter',
      'organizationFilter',
      'technologiesItems',
      'technologiesRefId',
      'technologiesRunningInterval',
      'technologiesProgress',
    ]),
    messageForNoData() {
      return this.hasFilter
        ? this.$t('technologies.no.data.message')
        : this.$t('no.filter.infrastructure.no.data.message');
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.technologies;
    },
    pages() {
      return this.technologiesPaginationState?.pageCount || Math.ceil(this.technologiesItems.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    technologiesFilteredItems() {
      const finalItems = [];
      for (let i = 0; i < this.technologiesItems.length; i += 1) {
        finalItems.push({ id: i + 1, ...this.technologiesItems[i] });
      }
      return finalItems;
    },
    tableColumnsTechnologies() {
      const columns = [
        technologiesTabTableColumns.technology,
        technologiesTabTableColumns.version,
        technologiesTabTableColumns.assets,
        technologiesTabTableColumns.existsExploits,
        technologiesTabTableColumns.cves,
        technologiesTabTableColumns.validated,
        // bugfix/CYN-1789-multiple-ui-currections, remove validation icon from row
        // technologiesTabTableColumns.action,
      ].map(column => ({
        ...column,
        title: column.title && this.$t(column.title),
        sort: this.technologiesListSort[column.sortingField || column.field],
      }));
      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    tableRowCellComponentsHashTechnologies() {
      return {
        technology: TechnologyNameTableCell,
        cves: TechnologiesCvesAmountTableCell,
        version: TechnologyVersionTableCell,
        assets: TechnologyAssetsAmountTableCell,
        existsExploits: TechnologyExploitIndicationTableCell,
        trendy: TechnologyTrendTableCell,
        validated: TechnologyValidationTriggeredTableCell,
        // bugfix/CYN-1789-multiple-ui-currections, remove validation icon from row
        // action: TechnologyValidateActionTableCell,
        default: WombatRowDefaultCell,
      };
    },
  },
  watch: {
    async currentTab() {
      if (this.currentTab === 'technologies' && this.domainFilter.length && !this.firstTimeCalled) {
        this.firstTimeCalled = true;
        await this.loadTechnologies();
      }
      this.assetsListPopup = false;
    },
    async domainFilter() {
      if (this.currentTab !== 'technologies' && this.domainFilter.length) {
        this.firstTimeCalled = false;
      }
      if (this.currentTab === 'technologies' && this.domainFilter.length) {
        this.approveLoadingOnSearchChange = false;
        this.callCounter = 1;
        this.page = 1;
        this.search = '';
        await this.loadTechnologies();
        this.callCounter = 0;
      }
    },
    async sortTechnologies() {
      this.page = 1;
      await this.loadTechnologies();
    },
    async hasExploit() {
      if (this.callCounter === 0) {
        this.page = 1;
        await this.loadTechnologies();
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.technologies-tab {
  padding: $space-4 $space-8;
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display

  &__footer {
    padding-top: $space-5;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $space-5;
    margin-top: rem(-25);

    &__left {
      &__filter {
        display: flex;

        &__switch {
          padding-left: $space-12;
          margin-top: $space-2;
        }
      }
    }

    &__right {
      margin-top: $space-4;

      &__csv {
        color: var(--text-primary);
        font-family: $default-application-font-medium;
        font-size: $font-size-sm;
        height: $btn-height-md;
        letter-spacing: 1.1px;
        line-height: $font-size-md;
        width: $btn-height-md;

        &:hover {
          cursor: pointer;
        }

        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  }

  :deep(.search-filter) {
    box-shadow: none;
    border: 1px solid var(--border-primary);
    border-radius: $radius-sm;
    margin-top: $space-4;
  }
}

.techs-tab-loading {
  align-self: center;
  justify-self: center;
  margin-top: 20%;
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

// Unscoped styles for WombatTable child component overrides
.technologies-tab {
  .wombat-table {
    --displayactioncell: none;
    padding: $space-4 0;

    .row-cell {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: center;
    }

    .column-technology .row-cell {
      justify-content: flex-start;
    }

    .wombat-row {
      border: none;
      border-bottom: 1px solid var(--border-primary);
    }

    .wombat-overlay {
      border-bottom: none;

      &:hover {
        background-color: var(--bg-tertiary);
        --display: block;
        --bg: var(--bg-tertiary);
        --displayactioncell: flex;
      }
    }

    .wombat-header {
      padding-bottom: $space-4;
    }
  }

  .column-technology {
    width: 17%;
  }

  .column-version,
  .column-assets,
  .column-existsexploits,
  .column-cves,
  .column-validated {
    width: 15%;
    display: flex;
    justify-content: center;

    .content {
      display: flex;
      justify-content: center;
    }
  }

  .column-column0 {
    min-width: rem(32);
    max-width: rem(32);
  }

  .column-column8 {
    min-width: rem(32);
    max-width: rem(32);
  }
}
</style>
